import Component from '@glimmer/component'
import { action } from '@ember/object'
import { steps } from 'customer-flows/components/_config/steps'
import { next } from '@ember/runloop'

type Page = Omit<BasePage, '__typename'>
type Step = Omit<BaseStep, '__typename'>

type StepSignature<S extends StepConfig, T = FlowStep> = {
  Args: Prettify<{
    register: (_step: FlowStep, _hooks: Record<FlowHook, Function>) => void
    unregister: (_step: FlowStep) => void
    updateFlow: (_step?: FlowStep) => void
    step: T
    stepSettings: Prettify<Omit<S, keyof Step>>
    pageSettings: Prettify<DistributiveOmit<PagesForStep<StepName<S>>, keyof Page>>
    scrollToNextStep: () => void
  }>
}

export default abstract class StepComponent<S extends StepConfig> extends Component<StepSignature<S>> {
  constructor(owner: unknown, args: any) {
    super(owner, args)

    const hooks: Record<FlowHook, Function> = {
      onStepMounted: this.onStepMounted.bind(this),
      onFlowUpdated: this.onFlowUpdated.bind(this),
      canUnmountStep: this.canUnmountStep.bind(this),
      canAdvanceStep: this.canAdvanceStep.bind(this),
      canAdvancePage: this.canAdvancePage.bind(this),
      onErrorCaptured: this.onErrorCaptured.bind(this)
    }

    next(() => {
      this.args.register(this.args.step, hooks)
    })
  }

  willDestroy() {
    super.willDestroy()
    this.args.unregister(this.args.step)
  }

  get step(): FlowStep {
    return this.args.step
  }

  /**
   * Triggers update on the flow-manager.
   * Must be called in order to trigger lifecycle hooks.
   */
  @action
  updateFlow() {
    this.args.updateFlow()
  }

  /**
   * Useful for setting up any initial state.
   * - Called right after the step is added to the DOM & has been registered with the flow.
   * - Called after the component's constructor.
   * - Will be called again if the step is re-activated (i.e. the user goes back to a previous step and then forward again).
   */
  protected onStepMounted(): Promise<void> | void {}

  /**
   * - Called when the flow is updated (i.e. transitions to a new step within a page).
   * - Called on all active steps except the current step and the step that initiated the transition.
   * - Current step will receive onStepMounted.
   * - Useful for checking error states or emitting signals.
   *
   * @returns Return false to notify the flow manager that the step is in an invalid state.
   */
  protected onFlowUpdated(): Promise<boolean | void> | boolean | void {}

  /**
   * This hook acts as a navigation guard and determines if the transition to the previous step should proceed.
   * - Called right before transitioning to the previous step.
   * - Not called when transitioning to the next step (use canAdvanceStep for that).
   * - Not called when transitioning between pages (use canAdvancePage for that).
   * @returns Return false to block the transition. (must be a boolean. null or undefined will coerce to true)
   */
  protected canUnmountStep(): Promise<boolean | void> | boolean | void {}

  /**
   * This hook acts as a navigation guard and determines if the transition to the next step should proceed.
   * - Called right before transitioning to the next step.
   * - Called after onStepMounted to check for falthrough (i.e. the next step can already be rendered).
   * - Not called when transitioning between pages (use canAdvancePage for that).
   * - Not called when transitioning to the previous step (use canUnmountStep for that).
   * @returns Return false to block the transition. (must be a boolean. null or undefined will coerce to true)
   */
  protected canAdvanceStep(): Promise<boolean | void> | boolean | void {}

  /**
   * This hook acts as a navigation guard and determines if the transition to the next page should proceed.
   * - Called right before the flow's page is transitioned to a new page.
   * - Not called when transitioning to the next step (use canAdvanceStep for that).
   * @returns Return false to block the transition, keeping the step mounted. (must be a boolean. null or undefined will coerce to true)
   */
  protected canAdvancePage(): Promise<boolean | void> | boolean | void {}

  /**
   * - Called when an error is thrown in any lifecycle hook of any step within the flow.
   * - This hook is triggered for all active steps **except** the step where the error originated.
   * - It also propagates the error to the route's `onErrorCaptured` hook.
   * - Allows steps/routes to respond to errors that occur in other steps, enabling cross-step error management.
   * @param _error - The original error
   * @returns Return true to halt further propagation to other steps.
   */
  protected onErrorCaptured(_error: Error): Promise<boolean | void> | boolean | void {}
}

// Distributive Omit for unions
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never

// Get the registry entry type for a StepType
type EntryFor<T extends StepType> = Extract<(typeof steps)[number], { type: T }>

// Derive supported page-name literals only from entries that have `supportedPages`
type SupportedPageNames<T extends StepType> = EntryFor<T> extends infer E
  ? E extends { supportedPages: readonly (infer N)[] } // only the entries that actually have the prop
    ? N
    : never
  : never

// Map those names to the concrete PageConfig members,
//    and default to ALL pages when no `supportedPages` existed.
type PagesForStep<T extends StepType> = [SupportedPageNames<T>] extends [never]
  ? PageConfig
  : Extract<PageConfig, { __typename: SupportedPageNames<T> }>

// Pull the step's discriminant off S
type StepName<S extends StepConfig> = S extends { __typename: infer N }
  ? Extract<N, StepType> // ensure it's one of StepType
  : never
